/* Duyduğunu Çal (gitar kulak eğitimi) — ders içerikleri. Blok türleri ve satır içi işaretler: ./blocks.ts */
import { compare, go, h, type Lesson, list, p, play, steps, tip } from './blocks';

export const gitar: Lesson[] = [
  {
    id: 'g1',
    title: 'Kulak eğitimi nasıl çalışır?',
    minutes: 4,
    summary: `Duyduğunu çalabilmek doğuştan gelen bir yetenek değil, çalışılarak gelişen bir beceridir; bu bölümdeki çalışma döngüsünü tanı.`,
    blocks: [
      p(
        `Bir melodiyi duyup gitarda hemen bulabilen insanlar genelde "doğuştan yetenekli" sanılır. Oysa bu becerinin büyük kısmı **göreceli kulaktır**: notaların birbirine olan uzaklığını duyabilmek. Göreceli kulak, düzenli çalışmayla herkeste gelişir.`,
      ),
      tip(
        'info',
        'Mutlak kulak şart değil',
        `Mutlak kulak, hiçbir referans olmadan bir notanın adını söyleyebilmektir ve çok az kişide bulunur. Duyduğunu çalmak için buna ihtiyacın yok: ilk notayı bulduktan sonra gerisini aralıklarla, yani göreceli kulakla çözersin.`,
      ),
      h(`Çalışma döngüsü`),
      steps([
        ['Dinle', `Melodiyi bir iki kez dinle. Bu aşamada gitara dokunma, yalnızca aklında tut.`],
        [
          'İçinden söyle',
          `Melodiyi mırıldan ya da içinden söyle. Söyleyebildiğin bir melodiyi sapta çok daha kolay bulursun.`,
        ],
        [
          'Sapta ara',
          `İlk notayı tek bir telde perde perde kaydırarak ara. Sesin mırıldandığın notaya "oturduğu" anı yakala.`,
        ],
        ['Çal', `Bulduğun notadan başlayarak melodiyi baştan sona çal.`],
        [
          'Karşılaştır',
          `Uygulama seni mikrofonla dinler ve hangi notanın doğru, hangisinin yanlış olduğunu gösterir. Yanlış nota da bilgi verir: hedefin daha yukarıda mı aşağıda mı olduğunu söyler.`,
        ],
      ]),
      play('Örnek melodiyi dinle', { seq: 'G3 A3 B3 D4 B3 A3 G3:2', bpm: 90 }),
      h(`Mikrofon ve kulaklık`),
      list([
        `Antrenmanlarda uygulama çaldığın notaları mikrofonla dinler. Ses kaydı yapılmaz; ses yalnızca tarayıcında anlık olarak analiz edilir.`,
        `Mümkünse **kulaklık** kullan. Hoparlörden gelen ses mikrofona karışmaz, sonuçlar daha güvenilir olur.`,
        `Gitarın akortlu olsun. Başlamadan önce akort aletiyle kısa bir kontrol yap.`,
        `Sessiz bir ortam seç. Arka plandaki müzik ya da televizyon sesi algılamayı zorlaştırır.`,
      ]),
      tip(
        'success',
        'Az ama her gün',
        `Günde 10–15 dakika çalışmak, haftada bir gün iki saat çalışmaktan çok daha etkilidir. Kulak, kısa ve sık tekrarlarla gelişir.`,
      ),
      go('Önce gitarını akort et', 'araclar.akort'),
      go('Seviye 1 ile başla', 'gitar.egz.1'),
    ],
  },
  {
    id: 'g2',
    title: 'Yukarı mı, aşağı mı?',
    minutes: 4,
    summary: `Bir notanın öncekinden ince mi kalın mı olduğunu ayırt etmek, melodi çıkarmanın ilk adımıdır.`,
    blocks: [
      p(
        `Müzikte "yukarı" ince (tiz) sese, "aşağı" kalın (pes) sese doğru gitmek demektir. Bir melodiyi çıkarırken her notada önce şu soruyu sorarsın: **bir önceki notadan yukarıda mı, aşağıda mı?**`,
      ),
      compare([
        ['Yukarı çıkan', { seq: 'C4 G4' }],
        ['Aşağı inen', { seq: 'G4 C4' }],
      ]),
      h(`Gitarda yön`),
      list([
        `Aynı telde **gövdeye doğru** ilerledikçe, yani perde numarası büyüdükçe, ses incelir.`,
        `Aynı perdede **ince tellere** geçtikçe ses incelir. 6. tel en kalın, 1. tel en ince teldir.`,
        `Bu yüzden yukarı gitmenin iki yolu vardır: aynı telde ilerlemek ya da daha ince bir tele geçmek.`,
      ]),
      {
        t: 'fretboard',
        notes: 'E2 E3 E4',
        from: 0,
        to: 12,
        caption: `Aynı nota üç farklı yükseklikte: {{n:E2}}, {{n:E3}} ve {{n:E4}}. Aynı yükseklikteki bir ses sapta birden fazla yerde bulunabilir.`,
      },
      compare([
        ['6. tel boş', { seq: 'E2' }],
        ['4. tel 2. perde', { seq: 'E3' }],
        ['1. tel boş', { seq: 'E4' }],
      ]),
      {
        t: 'quiz',
        q: `İkinci nota birinciye göre nerede?`,
        play: { seq: 'A3 E4' },
        options: ['Daha ince (yukarı)', 'Daha kalın (aşağı)'],
        answer: 0,
        explain: `{{n:A3}} notasından {{n:E4}} notasına çıktık: ikinci nota daha ince.`,
      },
      {
        t: 'quiz',
        q: `Peki bu sefer?`,
        play: { seq: 'D4 G3' },
        options: ['Daha ince (yukarı)', 'Daha kalın (aşağı)'],
        answer: 1,
        explain: `{{n:D4}} notasından {{n:G3}} notasına indik: ikinci nota daha kalın.`,
      },
      tip(
        'info',
        'Sesinle kontrol et',
        `Yönü kestiremediğinde iki notayı sırayla mırıldan. Sesinin yükseldiğini ya da alçaldığını gırtlağında hissedebilirsin.`,
      ),
      go('Tek tel antrenmanına geç', 'gitar.egz.1'),
    ],
  },
  {
    id: 'g3',
    title: 'Yarım ses, tam ses',
    minutes: 5,
    summary: `Perdeler arasındaki mesafeyi ses olarak tanımak, notayı bulmak için kaç perde gitmen gerektiğini söyler.`,
    blocks: [
      p(
        `Gitarda iki komşu perde arasındaki mesafe **yarım ses**tir. İki perde ilerlersen **tam ses** gitmiş olursun. Batı müziğindeki en küçük adım yarım sestir; diğer bütün aralıklar yarım seslerin toplamıdır.`,
      ),
      compare([
        ['Yarım ses (1 perde)', { seq: 'E4 F4' }],
        ['Tam ses (2 perde)', { seq: 'E4 F#4' }],
      ]),
      h(`Adım mı, atlama mı?`),
      p(
        `Melodiler çoğunlukla **adım adım** ilerler: yarım ya da tam ses. Arada bir de **atlama** yaparlar: üç, dört, beş yarım ses ya da daha fazlası. Bir sonraki notanın adım mı atlama mı olduğunu duymak, sapta nereye bakacağını büyük ölçüde daraltır.`,
      ),
      compare([
        ['Adımlar', { seq: 'G3 A3 B3 C4 B3 A3 G3' }],
        ['Atlamalar', { seq: 'G3 D4 B3 G4 E4 C4 G3' }],
      ]),
      h(`Majör gamın kalıbı`),
      p(
        `Majör gam şu kalıpla ilerler: **T – T – Y – T – T – T – Y** (T = tam ses, Y = yarım ses). Aynı kalıbı tek bir telde perde sayarak çalabilirsin. Örneğin 5. telde boş telden başlarsan perdeler 0 – 2 – 4 – 5 – 7 – 9 – 11 – 12 olur ve {{pc:A}} majör gamını çalarsın.`,
      ),
      play('5. telde La majör gamı', { seq: 'A2 B2 C#3 D3 E3 F#3 G#3 A3:2', bpm: 100 }),
      tip(
        'info',
        'Aynı kalıp her yerde',
        `Kalıbı hangi perdeden başlatırsan o notanın majör gamını çalarsın. 6. telde 3. perdeden başlarsan {{pc:G}} majör, 8. perdeden başlarsan {{pc:C}} majör çıkar.`,
      ),
      {
        t: 'quiz',
        q: `Bu iki nota arasında kaç perde var?`,
        play: { seq: 'B3 C4' },
        options: ['1 perde (yarım ses)', '2 perde (tam ses)', '3 perde'],
        answer: 0,
        explain: `{{pc:B}} ile {{pc:C}} arasında yarım ses vardır. Doğal notalar arasında yarım ses olan iki yerden biri burasıdır; diğeri {{pc:E}} ile {{pc:F}} arası.`,
      },
      {
        t: 'quiz',
        q: `Peki bunlar arasında?`,
        play: { seq: 'D4 F4' },
        options: ['1 perde', '2 perde', '3 perde'],
        answer: 2,
        explain: `{{pc:D}} ile {{pc:F}} arasında 3 yarım ses var, yani bir küçük 3'lü. Bu artık bir adım değil, küçük bir atlama.`,
      },
      go('İki nota antrenmanı', 'gitar.egz.3'),
    ],
  },
  {
    id: 'g4',
    title: 'Melodiyi sapta bulma yöntemi',
    minutes: 6,
    summary: `Duyduğun bir melodiyi rastgele denemelerle değil, adım adım bir yöntemle sapa dökmeyi öğren.`,
    blocks: [
      p(
        `Melodi çıkarmak deneme yanılmaya dayanır, ama rastgele deneme değildir. Aşağıdaki yöntem, her yanlış denemeyi bir sonraki doğru tahmine dönüştürür.`,
      ),
      steps([
        [
          'İlk notayı tek telde ara',
          `Melodinin ilk notasını mırıldan. Sonra tek bir telde, örneğin 2. telde, perde perde kaydırarak çal. Ses mırıldandığın notaya oturduğunda dur.`,
        ],
        ['Yönü belirle', `Sonraki nota yukarıda mı, aşağıda mı? Çalmadan önce buna karar ver.`],
        [
          'Mesafeyi tahmin et',
          `Küçük bir adım mı (1–2 perde), yoksa bir atlama mı (3 perde ve üstü)? Önce en yakın adayları dene.`,
        ],
        [
          'Önce tek telde çal',
          `Melodinin tamamını tek bir telde, yatay olarak bul. Tek telde perde saymak çok kolaydır.`,
        ],
        [
          'Pozisyona taşı',
          `Melodi oturduktan sonra onu elinin fazla kaymadığı bir pozisyona taşı. Bir kalın tele geçtiğinde aynı nota 5 perde ileridedir; yalnızca 2. telden 3. tele geçerken 4 perde ileridedir.`,
        ],
        ['Parça parça çalış', `Uzun melodileri 3–4 notalık parçalara böl. Bir parçayı oturtmadan diğerine geçme.`],
      ]),
      tip(
        'success',
        'Yanlış nota da bilgidir',
        `Yanlış bir nota çaldığında hemen vazgeçme. Kendine sor: hedef bundan yukarıda mı aşağıda mı? Uygulama da yanlış notalarda sana çaldığın notayı gösterir.`,
      ),
      h(`Bir örnekle deneyelim`),
      p(`Aşağıdaki kısa melodi 2. telin ilk beş perdesinde çalınabilir. İlk notası boş teldir: {{n:B3}}.`),
      play('Melodiyi dinle', { seq: 'B3 C4 D4 E4 D4 C4 B3:2', bpm: 80 }),
      list([
        `İlk nota boş tel. İkinci nota çok yakın ve yukarıda: 1. perde.`,
        `Üçüncü nota yine yukarıda ama bu kez bir tam ses: 3. perde.`,
        `Dördüncü nota bir tam ses daha yukarıda: 5. perde. Sonra aynı yoldan geri inersin.`,
      ]),
      play('Yavaş tekrar', { seq: 'B3 C4 D4 E4 D4 C4 B3:2', bpm: 60 }),
      tip(
        'info',
        'Mırıldanmak hile değil',
        `Deneyimli müzisyenler de bilmedikleri bir melodiyi önce içlerinden söyler. Sesinle bulduğun notayı gitarda bulmak, doğrudan kulaktan sapa gitmekten daha kolaydır.`,
      ),
      go('Üç notalık motifler', 'gitar.egz.4'),
      go('Dört nota, majör gam', 'gitar.egz.5'),
    ],
  },
  {
    id: 'g5',
    title: 'Tonaliteyi bil, işin yarısı biter',
    minutes: 6,
    summary: `Melodiler çoğunlukla bir gamın içinden çıkar; gamı ve "ev" notasını bilmek denemen gereken notaları azaltır.`,
    blocks: [
      p(
        `Bir şarkının melodisi 12 notanın hepsini rastgele kullanmaz. Genellikle bir gamın 5 ya da 7 notası içinde kalır. Gamı bilirsen sapta bakman gereken yer de küçülür.`,
      ),
      h(`Ev notası: tonik`),
      p(
        `Her tonalitenin bir merkezi vardır: **tonik**. Melodi toniğe vardığında "eve döndük" hissi verir ve çoğu melodi orada biter. Bir melodiyi çıkarmaya başlamadan önce bu notayı bul: sonda dinlenen, en huzurlu nota genelde odur.`,
      ),
      compare([
        ['Evde biten', { seq: 'E4 D4 C4 D4 E4 D4 C4:2' }],
        ['Havada kalan', { seq: 'E4 D4 C4 D4 E4 F4 D4:2' }],
      ]),
      {
        t: 'quiz',
        q: `Hangisi "tamamlandı" hissi veriyor?`,
        items: [
          { label: 'A', seq: 'G4 E4 F4 D4 C4:2' },
          { label: 'B', seq: 'G4 E4 F4 D4 B3:2' },
        ],
        options: ['A', 'B'],
        answer: 0,
        explain: `A, {{pc:C}} majörün toniğiyle bitiyor. B ise {{pc:B}} notasında kalıyor ve {{pc:C}} notasına çıkmak istiyor.`,
      },
      h(`İlk pozisyonda pentatonik`),
      p(
        `Pentatonik gam beş notalıdır; rock, blues, pop ve halk müziğinde en çok kullanılan gamdır. Aşağıdaki kalıp ilk pozisyonda {{pc:E}} minör pentatoniği gösterir: {{pc:E}}, {{pc:G}}, {{pc:A}}, {{pc:B}}, {{pc:D}}. Aynı beş nota {{pc:G}} majör pentatoniktir; fark, hangi notayı ev olarak duyduğundadır.`,
      ),
      {
        t: 'fretboard',
        pcs: 'E G A B D',
        root: 'E',
        from: 0,
        to: 3,
        caption: `{{pc:E}} minör pentatonik (aynı notalar {{pc:G}} majör pentatoniktir), 0–3. perdeler. Vurgulu notalar {{pc:E}}.`,
      },
      h(`5. perdede La minör pentatonik`),
      p(
        `Gitarcıların en iyi bildiği kalıp, 5. perdedeki {{pc:A}} minör pentatoniktir: {{pc:A}}, {{pc:C}}, {{pc:D}}, {{pc:E}}, {{pc:G}}. Riff ve solo çıkarırken çoğu zaman ilk bakacağın yer burasıdır.`,
      ),
      {
        t: 'fretboard',
        pcs: 'A C D E G',
        root: 'A',
        from: 5,
        to: 8,
        caption: `{{pc:A}} minör pentatonik, 5–8. perdeler. Vurgulu notalar {{pc:A}}.`,
      },
      play('Kalıbı kalından inceye dinle', { seq: 'A2 C3 D3 E3 G3 A3 C4 D4 E4 G4 A4 C5:2', bpm: 110 }),
      tip(
        'info',
        'Önce toniği bul',
        `Melodinin bittiği notayı mırıldan ve sapta bul. Diğer notaları bu notaya göre düşün: "tonikten bir adım yukarı", "tonikten bir 5'li yukarı" gibi.`,
      ),
      go('Beş–altı notalık melodiler', 'gitar.egz.6'),
    ],
  },
  {
    id: 'g6',
    title: "Riff'ler ve kalın teller",
    minutes: 5,
    summary: `Riff çıkarırken kalın tellerde minör pentatonik ve blues gamını kullanmayı, ritmi de notalar kadar önemsemeyi öğren.`,
    blocks: [
      p(
        `Riff, bir şarkıda tekrar eden kısa ve akılda kalıcı bir gitar kalıbıdır. Çoğu riff kalın tellerde (6., 5. ve 4. tel) çalınır ve çoğunlukla minör pentatonik ya da blues gamından gelir.`,
      ),
      h(`Kalın tellerde minör pentatonik`),
      p(
        `Boş tellerle birlikte {{pc:E}} minör pentatonik kalın tellerde şöyle durur: 6. telde 0 ve 3. perde ({{pc:E}}, {{pc:G}}), 5. telde 0 ve 2. perde ({{pc:A}}, {{pc:B}}), 4. telde 0 ve 2. perde ({{pc:D}}, {{pc:E}}). Blues gamı buna bir nota ekler: 5. telin 1. perdesi ({{pc:Bb}}). Bu notaya **blues notası** denir.`,
      ),
      {
        t: 'fretboard',
        pcs: 'E G A Bb B D',
        root: 'E',
        from: 0,
        to: 3,
        caption: `{{pc:E}} blues gamı, 0–3. perdeler. Riffler için kalın üç teli takip et.`,
      },
      play('Örnek riff', { seq: 'E2 E2 G2 A2 E2 G2 Bb2 A2:2', bpm: 100 }),
      h(`Ritim notalar kadar önemli`),
      p(
        `Bir riffi doğru notalarla ama yanlış ritimle çalarsan riff tanınmaz hale gelir. Notaları ararken riffi dizine vurarak ya da "ta-ta-taa" diye söyleyerek ritmini de ezberle.`,
      ),
      steps([
        ['Ritmi ezberle', `Riffi notaları düşünmeden, yalnızca ritim olarak tekrar et.`],
        [
          'En kalın notayı bul',
          `Riffler genelde toniğe döner. En sık duyduğun ve en kalın notayı 6. ya da 5. telde ara.`,
        ],
        [
          'Pentatonik kalıpta ara',
          `Diğer notaları önce minör pentatonik kalıbında dene. Tutmayan bir nota varsa blues notasını ya da yarım ses komşularını dene.`,
        ],
        ['Birleştir', `Notaları doğru ritimle çal ve duyduğunla karşılaştır.`],
      ]),
      play('Bir riff daha', { seq: 'A2 C3 D3 A2 C3 Eb3 D3 C3 A2:2', bpm: 96 }),
      tip(
        'info',
        'Oktav esnekliği',
        `Ayarlarda oktav esnekliği açıksa, bir notayı bir oktav yukarıda çalman da doğru sayılır. Yine de riffin karakteri için duyduğun oktavı yakalamaya çalış.`,
      ),
      go('Riff modu', 'gitar.egz.8'),
      go('Hayatta Kal meydan okuması', 'gitar.meydan.hayatta'),
    ],
  },
];
